/* eslint-disable no-unused-vars */
import ApiService from "../api.service";
import appConfig from "../../../app-config";

ApiService.init(appConfig.VUE_APP_ROOT_API);

/**
 * MediaBlobMeta size-variant helpers, shared by product-media-tab.vue and
 * gc-popup-product-media-pick-existing.vue. A ProductMediaFile has either 3
 * variants (original/preview/thumb - images) or 1 (original only - every
 * other allowed content type), per technical-design.md #12.
 * */
function getMediaBlobMeta(file, sizeType) {
  return ((file && file.MediaBlobMetas) || []).find(function (m) {
    return m.SizeType === sizeType;
  });
}

function isImageFile(file) {
  return !!getMediaBlobMeta(file, "thumb");
}

function getThumbUrl(file) {
  var variant = getMediaBlobMeta(file, "thumb") || getMediaBlobMeta(file, "original");
  return variant ? variant.MediaUrl : "";
}

/**
 * Converts a devextreme-style filter array (as emitted by gc-field-date's
 * valueChangeCallBack, e.g. [[dataField, ">=", date], "and", [dataField, "<", date]])
 * into OData $filter clauses.
 *
 * gc-field-date's non-UTC path (used here - see uploadedDateBoxOptions) builds each
 * boundary as `new Date("YYYY-MM-DDTHH:MM:SS")` - a date-time string with no timezone
 * designator, which the ECMAScript Date parser treats as local time. Its local getters
 * therefore already hold the exact calendar values the user selected. Formatting those
 * getters directly into a literal "...Z" string (bug #75501) is what's required here -
 * calling .toISOString() would instead re-convert through the browser's real UTC offset,
 * shifting the boundary by that offset and, once it exceeds a day fraction, onto the
 * wrong calendar day entirely.
 * */
const DATE_FILTER_ODATA_OPERATORS = { "=": "eq", "<": "lt", ">": "gt", "<=": "le", ">=": "ge" };
function toLiteralUtcIsoString(value) {
  var date = new Date(value);
  var pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.000Z`;
}
function dateFilterToODataClauses(filterArray) {
  return (filterArray || [])
    .filter((entry) => Array.isArray(entry))
    .map(([field, operator, value]) => {
      var odataOperator = DATE_FILTER_ODATA_OPERATORS[operator] || operator;
      return `${field} ${odataOperator} ${toLiteralUtcIsoString(value)}`;
    });
}

function getPreviewUrl(file) {
  var variant = getMediaBlobMeta(file, "preview") || getMediaBlobMeta(file, "original");
  return variant ? variant.MediaUrl : "";
}

function getOriginalContentType(file) {
  var variant = getMediaBlobMeta(file, "original");
  return variant ? variant.ContentType : "";
}

const ProductMediaService = {
  /**
   * Get Screen data
   *
   * */
  async getProductByCode(code) {
    const requestScreenData = {
      url: "api/Product",
      top: "1",
      filter: `Code eq '${code}'`,
      select: "Code,Description1",
    };

    return await ApiService.oDataGet(requestScreenData);
  },

  /**
   * Uploads a new file and immediately associates it with the given product (UC1).
   * */
  async uploadFile(payload) {
    return await ApiService.post("api/ProductMediaFile", payload);
  },

  /**
   * Feeds the Images/Documents galleries. Expands the real ProductMediaFile
   * navigation property, and its own MediaBlobMetas (one row per stored size
   * variant), so FileName and every original/preview/thumb variant come back
   * in one call (technical-design.md #12).
   * */
  async getAttachments(productCode) {
    const requestScreenData = {
      url: "api/ProductMediaAttachment",
      filter: `ProductCode eq '${productCode}'`,
      expand: "ProductMediaFile($expand=MediaBlobMetas)",
    };

    return await ApiService.oDataGet(requestScreenData);
  },

  /**
   * Marks the target image attachment as the product's Primary, atomically
   * un-marking whichever attachment was previously Primary (BR-2092-3).
   * */
  async setPrimary(payload) {
    return await ApiService.post("api/ProductMediaAttachment/SetPrimary", payload);
  },

  /**
   * Detaches this product's association to a shared file (BR-2092-2). Only
   * removes the requesting product's association - the underlying file is
   * removed server-side once no product retains an association to it.
   * */
  async detach(payload) {
    return await ApiService.post("api/ProductMediaAttachment/Detach", payload);
  },

  /**
   * Live count of every product currently associated with a file - computed at
   * removal time, not cached, since another product could detach concurrently.
   * */
  async getAssociationCount(productMediaFileRecordId) {
    const requestScreenData = {
      url: "api/ProductMediaAttachment",
      filter: `ProductMediaFileRecordID eq ${productMediaFileRecordId}`,
      count: true,
      top: "0",
    };

    return await ApiService.oDataGet(requestScreenData);
  },

  /**
   * Backs the Pick Existing popup. Never called unfiltered (BR-2092-5) - the
   * popup itself gates this client-side. Expands ProductMediaAttachments so
   * the grid's Product column/filter work from a single call.
   * */
  async searchFiles(filters) {
    var clauses = [];

    if (filters.search) {
      clauses.push(`contains(FileName,'${filters.search}')`);
    }
    if (filters.contentType) {
      // ContentType no longer lives directly on ProductMediaFile - it's on the
      // 'original' MediaBlobMeta variant (technical-design.md #12).
      clauses.push(
        `MediaBlobMetas/any(m: m/SizeType eq 'original' and m/ContentType eq '${filters.contentType}')`
      );
    }
    if (filters.productCodes && filters.productCodes.length > 0) {
      var productClauses = filters.productCodes.map(
        (code) => `ProductMediaAttachments/any(a: a/ProductCode eq '${code}')`
      );
      clauses.push(`(${productClauses.join(" or ")})`);
    }
    if (filters.uploadedFilter && filters.uploadedFilter.length > 0) {
      clauses.push(...dateFilterToODataClauses(filters.uploadedFilter));
    }

    const requestScreenData = {
      url: "api/ProductMediaFile",
      filter: clauses.join(" and "),
      expand: "ProductMediaAttachments,MediaBlobMetas",
    };

    return await ApiService.oDataGet(requestScreenData);
  },

  /**
   * Associates one or more existing files with a product in a single call -
   * Pick Existing's multi-select Select action (UC2).
   * */
  async associateFiles(payload) {
    return await ApiService.post("api/ProductMediaAttachment", payload);
  },
};

export {
  ProductMediaService,
  isImageFile,
  getThumbUrl,
  getPreviewUrl,
  getOriginalContentType,
};
